/**
 * LA NAVIGATION : CONTEXTE ET FOCUS (DEV-1508 lot 2, engagement E6).
 * ============================================================================
 *
 * INTENTION : on regarde une période, on en resserre un morceau au CLIQUER-GLISSER
 * (sur un graphe ou sur la bande de complétude), on revient d'un double-clic ou
 * du bouton « Revenir au contexte ». Rien d'autre : pas de boutons Zoomer, pas de
 * fenêtres préréglées, pas de fil d'Ariane (addendum du 09/09, mot pour mot :
 * « le zoom se fera au brush »).
 *
 * Une seule addition depuis, et c'est une porte d'accessibilité, pas un second
 * mode de navigation : les deux champs de dates de la barre (lot B5) écrivent la
 * même fenêtre que le cliquer-glisser, par le même chemin. Le clavier fait donc
 * enfin ce que la souris faisait déjà.
 *
 * Deux fenêtres, jamais trois :
 *  • le CONTEXTE, la période chargée, plafonnée par le dû. Il ne bouge pas ;
 *  • le FOCUS, la fenêtre regardée. Il vit à l'intérieur du contexte, il est
 *    partagé par tous les graphes de l'onglet et CONSERVÉ d'un onglet à l'autre.
 *
 * Ce module est pur : il traduit un geste en fenêtre, il ne charge rien. Le
 * niveau de résolution, lui, se déduit de la largeur du focus (`useSeriesEnedis`).
 *
 * ⚠️ Un brush ne rend JAMAIS une fenêtre hors du contexte, ni une fenêtre
 * inversée, ni une fenêtre de zéro jour : un geste maladroit ne doit pas produire
 * un écran vide qu'on lirait « ce compteur n'a pas de donnée ».
 */
import type { Fenetre } from "@modules/data/composables/useSeriesEnedis"

function versUtc(jour: string): number {
    return Date.UTC(
        Number(jour.slice(0, 4)),
        Number(jour.slice(5, 7)) - 1,
        Number(jour.slice(8, 10))
    )
}

function versJour(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10)
}

/** Le jour d'un horodate quelconque servi par le graphe (ISO ou `YYYY-MM-DD`). */
export function jourDe(
    valeur: string | number | Date | null | undefined
): string | null {
    if (valeur === null || valeur === undefined || valeur === "") return null
    if (typeof valeur === "string" && /^\d{4}-\d{2}-\d{2}/.test(valeur)) {
        return valeur.slice(0, 10)
    }
    const d = new Date(valeur)
    if (Number.isNaN(d.getTime())) return null
    return d.toISOString().slice(0, 10)
}

/** Vrai quand les deux fenêtres sont la même. Sert à savoir si on est au contexte. */
export function memeFenetre(a: Fenetre | null, b: Fenetre | null): boolean {
    if (!a || !b) return false
    return a.debut === b.debut && a.fin === b.fin
}

/**
 * La fenêtre d'un cliquer-glisser, bornée par le contexte.
 *
 * Rend `null` quand le geste est illisible (une borne non datable) : un focus
 * qu'on ne sait pas lire ne remplace pas celui qu'on regardait.
 */
export function fenetreDuBrush(
    borneA: string | number | Date | null | undefined,
    borneB: string | number | Date | null | undefined,
    contexte: Fenetre
): Fenetre | null {
    const a = jourDe(borneA)
    const b = jourDe(borneB)
    if (!a || !b) return null

    const [debut, fin] = versUtc(a) <= versUtc(b) ? [a, b] : [b, a]
    const borneBasse = Math.max(versUtc(debut), versUtc(contexte.debut))
    const borneHaute = Math.min(versUtc(fin), versUtc(contexte.fin))
    // Un geste entièrement hors du contexte ne recadre rien.
    if (borneBasse > borneHaute) return null
    return { debut: versJour(borneBasse), fin: versJour(borneHaute) }
}

/**
 * La fenêtre d'un cliquer-glisser sur la BANDE de complétude, dont les cases sont
 * les jours du contexte dans l'ordre. On y désigne des jours, pas des pixels :
 * deux index suffisent, et un index hors bande est ramené dans la bande.
 */
export function fenetreDeLaBande(
    indexA: number,
    indexB: number,
    jours: string[]
): Fenetre | null {
    if (jours.length === 0) return null
    const borne = (i: number) =>
        Math.min(Math.max(Math.trunc(i), 0), jours.length - 1)
    const a = borne(indexA)
    const b = borne(indexB)
    const [debut, fin] = a <= b ? [a, b] : [b, a]
    return { debut: jours[debut], fin: jours[fin] }
}

/** Le geste qui a recadré la vue, tel que le suivi le nomme (contrat §3.1). */
export type GesteRecadrage =
    | "brush"
    | "bande"
    /**
     * La fenêtre DÉPLACÉE à la main sur la bande, largeur conservée (lot 5e,
     * écart T9). Un geste de la main, comme le bouton de retour : il se
     * distingue du `bande` qui CHOISIT une fenêtre, et le suivi doit pouvoir
     * dire lequel des deux gestes sert.
     */
    | "bande_deplacer"
    /**
     * LE RETOUR AU CONTEXTE DEMANDÉ SUR LA BANDE. Depuis S3 (review du 23/09),
     * DEUX gestes y mènent : le double-clic, et le clic simple à côté de la
     * fenêtre.
     *
     * ⚠️ LE SUIVI NE LES DISTINGUE PAS, et ce n'est pas un oubli : le nom du
     * geste est validé contre `CompteurGesteRecadrage`, une enum FERMÉE du
     * contrat de suivi (`core/tracking/events.ts`, et `plan.yaml` avec elle),
     * hors du périmètre de ce lot. Y ajouter une valeur est le bon geste, il
     * appartient à un lot qui a le droit de toucher ce contrat. D'ici là, un
     * clic simple est compté ici avec les double-clics : le compte de ce geste
     * dit « retour au contexte depuis la bande », pas « double-clic », et il
     * ne faut pas le lire comme une mesure du double-clic seul.
     */
    | "double_clic"
    | "bouton"
    /** La saisie des deux champs de dates de la barre (DEV-1508 lot B5). */
    | "dates"
    /**
     * La touche Échap au clavier, hors geste sur la bande et hors champ de
     * saisie (lot 5e, écart T10) : même chemin que le bouton de retour, mais
     * le suivi doit pouvoir dire que la main n'a pas cliqué.
     */
    | "echap"

/** Écriture d'une fenêtre pour la charge utile du suivi : des dates, jamais des valeurs. */
export function fenetreTracee(fenetre: Fenetre | null): string | null {
    return fenetre ? `${fenetre.debut}/${fenetre.fin}` : null
}
